// import "core-js";
import '@babel/polyfill'
import bowser from 'bowser'
import React, { useState, useEffect } from 'react'
import Router from 'next/router'
import Head from 'next/head'
import App from 'next/app'
import dynamic from 'next/dynamic'
import Prismic from 'prismic-javascript'
import camelCase from 'lodash/camelCase'
import { repoName, analyticsGtm, preferredHost } from '../config'
import '../baseStyles.scss'
import GlobalContext from '../global'
import getRef from '../helpers/getRef'
import getLanguage, { convertPrismicToLang } from '../helpers/language'
import {
  gtmHead,
  persistContext,
  returnPrismicScript,
  returnCookieScript,
  trackGTM,
  animateLoaderOut,
  animateLoaderIn,
  checkForRedirects
} from '../helpers/appHelpers'
import Cookie from '../helpers/cookie'

Router.events.on('routeChangeStart', animateLoaderIn)
Router.events.on('routeChangeComplete', animateLoaderOut)
Router.events.on('routeChangeError', animateLoaderOut)
Router.events.on('routeChangeComplete', trackGTM)

export default function MyApp({ Component, pageProps, router, global, alternateLanguages }) {
  const [isIE, setIsIE] = useState(false)
  const [showNav, setShowNav] = useState(false)
  const persistedGlobal = persistContext(global)
  // TODO: properly get them!
  let title = 'Titel'
  let description = 'Desc'
  let image
  useEffect(() => {
    const browser = bowser.getParser(window.navigator.userAgent)
    setIsIE(camelCase(browser.parsedResult.browser.name) === 'internetExplorer')
    animateLoaderOut()
  })
  // const title = persistedGlobal.data.share_title
  // const description = persistedGlobal.data.share_description
  // const image = persistedGlobal.data.share_image
  return (
    <>
      <Head>
        <script dangerouslySetInnerHTML={gtmHead(analyticsGtm)} />
        <meta charSet="utf-8" />
        <meta httpEquiv="x-ua-compatible" content="ie=edge" />
        <meta name="robots" content="index,follow" />
        <meta name="googlebot" content="index,follow" />
        <meta name="geo.placename" content="Vienna, Austria" />
        <meta name="language" content={router.query.lang} />
        <meta property="og:locale" content={router.query.lang} />
        <meta name="rating" content="General" />
        <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
        <script src="/static/scripts/modernizr.js" />
        {alternateLanguages.length > 0 &&
          alternateLanguages.map(lang => (
            <link rel="alternate" href={lang.url} hreflang={lang.lang} key={`hreflang-${lang.lang}`} />
          ))}
        <link rel="dns-prefetch" href="//prismic.io" />
        <link rel="preconnect" href="//prismic.io" />

        <link rel="apple-touch-icon" sizes="180x180" href="/static/favicons/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/static/favicons/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/static/favicons/favicon-16x16.png" />
        <link rel="manifest" href="/static/favicons/site.webmanifest" />
        <link rel="mask-icon" href="/static/favicons/safari-pinned-tab.svg" color="#000000" />
        <link rel="shortcut icon" href="/static/favicons/favicon.ico" />
        <meta name="msapplication-TileColor" content="#ffffff" />
        <meta name="msapplication-config" content="/static/favicons/browserconfig.xml" />
        <meta name="theme-color" content="#ffffff" />

        <title>{title}</title>
        {description && <meta key="description" property="description" content={description} />}
        <meta key="og:type" property="og:type" content="website" />
        <meta key="og:site_name" property="og:site_name" content={title} />
        <meta key="og:title" property="og:title" content={title} />
        {description && <meta key="og:description" property="og:description" content={description} />}
        {image && <meta key="og:image" property="og:image" content={image.url} />}
        {image && image.dimensions && (
          <meta key="og:image:width" property="og:image:width" content={image.dimensions.width} />
        )}
        {image && image.dimensions && (
          <meta key="og:image:height" property="og:image:height" content={image.dimensions.height} />
        )}
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta key="twitter:title" name="twitter:title" content={title} />
        {description && <meta key="twitter:description" name="twitter:description" content={description} />}
        {image && <meta key="twitter:image" name="twitter:image" content={image.url} />}
        <link href="https://static.oebb.at/fonts/fonts.css" rel="stylesheet" />
        <script
          type="module"
          src="https://static.oebb.at/web-components-external/oebb-web-components-external.esm.js"
        />
        <script noModule src="https://static.oebb.at/web-components-external/oebb-web-components-external.js" />
      </Head>
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${analyticsGtm}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>

      <div className={`root ${isIE ? 'ie' : 'generic'}`}>
        <GlobalContext.Provider value={persistedGlobal}>
          <Component key={router.asPath} {...pageProps} />
        </GlobalContext.Provider>
      </div>
      <div className="loading" id="loading">
        <div className="inner">
          <div className="spinner white fixed" />
        </div>
      </div>
      <script dangerouslySetInnerHTML={returnPrismicScript()} />
      <script type="text/javascript" src="https://static.cdn.prismic.io/prismic.min.js" />
      <script src="https://cc.cdn.civiccomputing.com/8/cookieControl-8.x.min.js" />
      <Cookie language={persistedGlobal.lang.substring(0, 2)} />
    </>
  )
}

MyApp.getInitialProps = async appContext => {
  // before anything, check if we should redirect
  checkForRedirects(appContext.ctx)
  // now get the language by different methods
  let language = await getLanguage(appContext)
  // this only has to be done once on the server and not on the client anymore
  let global
  if (!process.browser) {
    // get global data that we need from prismic
    const api = await Prismic.getApi(`https://${repoName}.prismic.io/api/v2`)
    const ref = getRef(api, appContext.ctx)
    const globalQuery = await api.query(Prismic.Predicates.at('document.type', 'global'), {
      ref,
      lang: language
    })
    if (globalQuery && globalQuery.results && globalQuery.results.length > 0) {
      // eslint-disable-next-line prefer-destructuring
      global = globalQuery.results[0]
    }
  }
  // check if we should redirect (index page)
  if (appContext.router.route === '/' || appContext.router.route === '/[lang]') {
    appContext.ctx.res.writeHead(302, { Location: `/${language}/${global.data.home_page.uid}` })
    appContext.ctx.res.end()
  }
  // get the page props
  const appProps = await App.getInitialProps(appContext, global)
  // parse alternate languages
  let alternateLanguages = []
  if (appProps.pageProps.page && appProps.pageProps.page.alternate_languages.length > 0) {
    alternateLanguages = appProps.pageProps.page.alternate_languages.map(o => {
      let lang = convertPrismicToLang(o.lang)
      return {
        lang,
        url: `https://${preferredHost}/${lang}/${o.uid}`
      }
    })
  }
  // make these available in the global object to be picked up by the language switcher
  global.pageAlternateLanguages = alternateLanguages
  return { ...appProps, global, alternateLanguages }
}
